import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import {
  Link as LinkIcon,
  Unlink,
  Package,
  Truck,
  MapPin,
  Route,
  Thermometer,
  ArrowRight,
  CheckCircle2,
  Search } from
'lucide-react';
import { Input } from '@/shared/components/ui/input';

export const SensorBinding = () => {const { t } = useTranslation("ezparcel");
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [bindingType, setBindingType] = useState('');
  const [selectedTarget, setSelectedTarget] = useState('');

  const unassignedSensors = mockSensors.filter((s) => !s.assignedTo);
  const assignedSensors = mockSensors.filter((s) => s.assignedTo);

  const bindingTypeIcons = {
    'pallet': Package,
    'shipment': Truck,
    'vehicle': Truck,
    'route': Route
  };

  const getBindingOptions = () => {
    switch (bindingType) {
      case 'pallet':return mockBindingOptions.pallets;
      case 'shipment':return mockBindingOptions.shipments;
      case 'vehicle':return mockBindingOptions.vehicles;
      case 'route':return mockBindingOptions.routes;
      default:return [];
    }
  };

  return (
    <div className="space-y-6" data-testid="coldchain-binding">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <LinkIcon className="h-8 w-8 text-cyan-600" />{t("sensorBinding", "Sensor Binding")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("assignSensorsTo", "Assign sensors to pallets, shipments, vehicles, and routes")}

          </p>
        </div>
      </div>

      {/* Binding Wizard */}
      <Card className="card-elevated border-2 border-cyan-200 bg-gradient-to-r from-cyan-50/50 to-blue-50/50 dark:from-cyan-950/20 dark:to-blue-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-cyan-600" />{t("quickBindWizard", "Quick Bind Wizard")}

          </CardTitle>
          <CardDescription>{t("selectASensor", "Select a sensor and assign it to an asset")}

          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4 items-end">
            {/* Step 1: Select Sensor */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("1SelectSensor", "1. Select Sensor")}</label>
              <Select value={selectedSensor || ''} onValueChange={setSelectedSensor}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose sensor..." />
                </SelectTrigger>
                <SelectContent>
                  {unassignedSensors.map((sensor) =>
                  <SelectItem key={sensor.id} value={sensor.id}>
                      {sensor.serialNumber} ({sensor.name})
                    </SelectItem>
                  )}
                  {unassignedSensors.length === 0 &&
                  <SelectItem value="none" disabled>{t("noUnassignedSensors", "No unassigned sensors")}</SelectItem>
                  }
                </SelectContent>
              </Select>
            </div>

            {/* Step 2: Select Type */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("2BindingType", "2. Binding Type")}</label>
              <Select value={bindingType} onValueChange={setBindingType} disabled={!selectedSensor}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pallet">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4" />{t("pallet", "Pallet")}

                    </div>
                  </SelectItem>
                  <SelectItem value="shipment">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4" />{t("shipment", "Shipment")}

                    </div>
                  </SelectItem>
                  <SelectItem value="vehicle">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4" />{t("vehicle", "Vehicle")}

                    </div>
                  </SelectItem>
                  <SelectItem value="route">
                    <div className="flex items-center gap-2">
                      <Route className="h-4 w-4" />{t("route", "Route")}

                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Step 3: Select Target */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("3SelectTarget", "3. Select Target")}</label>
              <Select value={selectedTarget} onValueChange={setSelectedTarget} disabled={!bindingType}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose target..." />
                </SelectTrigger>
                <SelectContent>
                  {getBindingOptions().map((option) =>
                  <SelectItem key={option.id} value={option.id}>
                      {option.name}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Step 4: Bind */}
            <Button
              className="bg-cyan-600 hover:bg-cyan-700 gap-2"
              disabled={!selectedSensor || !bindingType || !selectedTarget}>
              
              <LinkIcon className="h-4 w-4" />{t("bindSensor", "Bind Sensor")}

            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Current Bindings */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />{t("currentBindings", "Current Bindings (")}
            {assignedSensors.length})
          </CardTitle>
          <CardDescription>{t("sensorsCurrentlyAssigned", "Sensors currently assigned to assets")}

          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {assignedSensors.map((sensor) => {
              const Icon = bindingTypeIcons[sensor.assignedTo?.type] || Package;
              return (
                <div
                  key={sensor.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-cyan-100">
                      <Thermometer className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div>
                      <p className="font-medium">{sensor.serialNumber}</p>
                      <p className="text-sm text-muted-foreground">{sensor.name}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <div className="p-2 rounded-lg bg-blue-100">
                      <Icon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">{sensor.assignedTo?.name}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs capitalize">
                          {sensor.assignedTo?.type}
                        </Badge>
                        <span className="text-sm text-muted-foreground font-mono">
                          {sensor.assignedTo?.id}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-mono text-lg">{(sensor.lastReading?.temp ?? 0).toFixed(1)}{t("c", "\xB0C")}</p>
                      <p className="text-xs text-muted-foreground">{t("current", "Current")}</p>
                    </div>
                    <Button variant="outline" size="sm" className="gap-1 text-red-600">
                      <Unlink className="h-3 w-3" />{t("unbind", "Unbind")}

                    </Button>
                  </div>
                </div>);

            })}
          </div>
        </CardContent>
      </Card>

      {/* Unassigned Sensors */}
      {unassignedSensors.length > 0 &&
      <Card className="card-elevated border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <Unlink className="h-5 w-5" />{t("unassignedSensors", "Unassigned Sensors (")}
            {unassignedSensors.length})
            </CardTitle>
            <CardDescription>{t("theseSensorsAre", "These sensors are available for assignment")}

          </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {unassignedSensors.map((sensor) =>
            <div
              key={sensor.id}
              className="p-4 rounded-lg border border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
              
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline">{sensor.id}</Badge>
                    <Badge className={
                sensor.status === 'Active' ? 'bg-emerald-100 text-emerald-700' :
                'bg-gray-100 text-gray-700'
                }>
                      {sensor.status}
                    </Badge>
                  </div>
                  <p className="font-mono text-sm">{sensor.serialNumber}</p>
                  <p className="text-sm text-muted-foreground">{sensor.name}</p>
                </div>
            )}
            </div>
          </CardContent>
        </Card>
      }

      {/* Binding Chain Visualization */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle>{t("bindingChainHierarchy", "Binding Chain Hierarchy")}</CardTitle>
          <CardDescription>{t("howSensorsFlow", "How sensors flow through the logistics chain")}

          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center gap-4 py-8">
            <div className="text-center">
              <div className="p-4 rounded-xl bg-cyan-100 mx-auto w-fit mb-2">
                <Thermometer className="h-8 w-8 text-cyan-600" />
              </div>
              <p className="font-medium">{t("sensor", "Sensor")}</p>
            </div>
            <ArrowRight className="h-6 w-6 text-muted-foreground" />
            <div className="text-center">
              <div className="p-4 rounded-xl bg-orange-100 mx-auto w-fit mb-2">
                <Package className="h-8 w-8 text-orange-600" />
              </div>
              <p className="font-medium">{t("pallet", "Pallet")}</p>
            </div>
            <ArrowRight className="h-6 w-6 text-muted-foreground" />
            <div className="text-center">
              <div className="p-4 rounded-xl bg-blue-100 mx-auto w-fit mb-2">
                <Truck className="h-8 w-8 text-blue-600" />
              </div>
              <p className="font-medium">{t("shipment", "Shipment")}</p>
            </div>
            <ArrowRight className="h-6 w-6 text-muted-foreground" />
            <div className="text-center">
              <div className="p-4 rounded-xl bg-violet-100 mx-auto w-fit mb-2">
                <Route className="h-8 w-8 text-violet-600" />
              </div>
              <p className="font-medium">{t("route", "Route")}</p>
            </div>
            <ArrowRight className="h-6 w-6 text-muted-foreground" />
            <div className="text-center">
              <div className="p-4 rounded-xl bg-emerald-100 mx-auto w-fit mb-2">
                <Truck className="h-8 w-8 text-emerald-600" />
              </div>
              <p className="font-medium">{t("vehicle", "Vehicle")}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>);

};